import { Grid } from '@material-ui/core';
import { MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';

import {
  deleteBillingDetailConfiguration,
  deleteBillingDetailTiers,
  getBillingDetailsConfiguration,
  getBillingDetailsTiers,
} from '~/src/dash/api/billing-detail/billing-detail-methods';
import {
  IBillingDetailConfiguration,
  IBillingDetailTiers,
} from '~/src/dash/api/billing-detail/billing-detail-models';
import { IPublisher } from '~/src/dash/api/console-api';
import { getPublishersSimplifiedV1 } from '~/src/dash/api/publisher';
import { EBool } from '~/src/dash/api/types/bool';
import {
  IBillingDetailConfigurationRowData,
  IBillingDetailTiersRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBillingDetailConfigurationExisting,
  RouteBillingDetailConfigurationNew,
  RouteBillingDetailTiersExisting,
  RouteBillingDetailTiersNew,
} from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { routeName } from '~/src/dash/utils/routes';
import { ButtonView } from '~/src/dash/views/button-view';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  loadingPublishers: boolean;
  loading: boolean;
  updating: boolean;
  snackbarMessage: string;
  snackbarError: boolean;
  activeFilter: string;
  publishers: Array<Partial<IPublisher>>;
  billingDetailsConfiguration: IBillingDetailConfiguration[];
  billingDetailsTiers: IBillingDetailTiers[];
  currentTab: number;
}

export const BillingDetailListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      loadingPublishers: false,
      loading: false,
      updating: false,
      snackbarMessage: '',
      snackbarError: false,
      activeFilter: 'Active',
      publishers: [] as Array<Partial<IPublisher>>,
      billingDetailsConfiguration: [] as IBillingDetailConfiguration[],
      billingDetailsTiers: [] as IBillingDetailTiers[],
      currentTab: 0,
    };

    componentDidMount() {
      this.getPublishers();
      this.getBillingDetailsConfiguration();
      this.getBillingDetailsTiers();
    }

    getPublishers = () => {
      getPublishersSimplifiedV1()
        .then((resp) => {
          const publishers = resp[0];
          if (publishers) {
            this.setState({
              publishers,
              loadingPublishers: false,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading publishers',
            loadingPublishers: false,
          });
        });
    };

    getBillingDetailsConfiguration = () => {
      getBillingDetailsConfiguration()
        .then((resp) => {
          const billingDetailsConfiguration = resp[0];
          if (billingDetailsConfiguration) {
            this.setState({
              billingDetailsConfiguration,
              loading: false,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading billing details configuration',
            loading: false,
          });
        });
    };

    getBillingDetailsTiers = () => {
      getBillingDetailsTiers()
        .then((resp) => {
          const billingDetailsTiers = resp[0];
          if (billingDetailsTiers) {
            this.setState({
              billingDetailsTiers,
              loading: false,
            });
          }
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error loading billing details tiers',
            loading: false,
          });
        });
    };

    getBillingDetailConfigurationRoute = (id: number): string => {
      return getRoutePath(RouteBillingDetailConfigurationExisting, extractRouteParams(this.props), {
        billingdetailconfiguration: id.toString(),
      });
    };

    getBillingDetailTiersRoute = (id: number): string => {
      return getRoutePath(RouteBillingDetailTiersExisting, extractRouteParams(this.props), {
        billingdetailtiers: id.toString(),
      });
    };

    // setSettingsTabsCurrentTab is passed to the SettingsTabs component to set the current tab
    setSettingsTabsCurrentTab = (currentTab: number) => {
      this.setState({
        currentTab,
      });
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // ************************************
    // ****** CRUD RELATED FUNCTIONS ******
    // ************************************

    createRouteBillingDetailConfiguration = () => {
      navigate(RouteBillingDetailConfigurationNew, extractRouteParams(this.props));
    };

    createRouteBillingDetailTiers = () => {
      navigate(RouteBillingDetailTiersNew, extractRouteParams(this.props));
    };

    deleteBillingDetailConfiguration = (billingDetailConfigurationID: string) => {
      this.setState(
        {
          updatingBillingDetailConfiguration: true,
        },
        () => {
          deleteBillingDetailConfiguration(billingDetailConfigurationID)
            .then(() => {
              const contextualSegments = this.state.billingDetailsConfiguration.filter(
                (Route: IBillingDetailConfiguration) =>
                  Route.ID.toString() !== billingDetailConfigurationID,
              );
              this.setState(
                {
                  contextualSegments,
                  snackbarMessage: 'Successfully deleted billing detail configuration',
                  snackbarError: false,
                  updatingBillingDetailConfiguration: false,
                },
                this.getBillingDetailsConfiguration,
              );
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting billing detail configuration',
                snackbarError: true,
                updatingBillingDetailConfiguration: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    deleteBillingDetailTiers = (billingDetailTiersID: string) => {
      this.setState(
        {
          updatingBillingDetailTiers: true,
        },
        () => {
          deleteBillingDetailTiers(billingDetailTiersID)
            .then(() => {
              const contextualSegments = this.state.billingDetailsTiers.filter(
                (Route: IBillingDetailTiers) => Route.ID.toString() !== billingDetailTiersID,
              );
              this.setState(
                {
                  contextualSegments,
                  snackbarMessage: 'Successfully deleted billing detail configuration',
                  snackbarError: false,
                  updatingBillingDetailTiers: false,
                },
                this.getBillingDetailsTiers,
              );
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                snackbarMessage: 'Error while deleting billing detail configuration',
                snackbarError: true,
                updatingBillingDetailTiers: false,
              });
            });
        },
      );
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // ************************************
    // ******** HANDLER FUNCTIONS *********
    // ************************************
    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.name === 'activeFilter') {
        this.setState({
          [target.name]: target.value,
          page: 0,
        });
      } else {
        this.setState({ [target.name]: target.value });
      }
    };

    handleSetUpdatingBillingDetailConfiguration = (updatingBillingDetailConfiguration: boolean) => {
      this.setState({ updatingBillingDetailConfiguration });
    };

    handleSetUpdatingBillingDetailTiers = (updatingBillingDetailTiers: boolean) => {
      this.setState({ updatingBillingDetailTiers });
    };

    generateBillingDetailName = (
      detail: IBillingDetailConfiguration | IBillingDetailTiers,
    ): string => {
      const publisher = this.state.publishers.filter(
        (pb: Partial<IPublisher>) => pb.ID === detail.PublisherID,
      );
      if (publisher.length > 0) {
        return `[${publisher[0].ID}] ${publisher[0].Name}`;
      }
      return `[${detail.PublisherID}]`;
    };

    // ************************************
    // ********* RENDER FUNCTIONS *********
    // ************************************

    // renderCustomBillingDetailsToolbar renders the route toolbar at the top of the list table
    renderCustomBillingDetailsToolbar = (props: any): ReactElement => {
      const { classes } = this.props;
      const { updating } = this.state;

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item className={classes.toolbarRow}>
          <Grid container direction='row' justify='space-between' alignItems='center'>
            {/* grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
            <Grid item>
              <Grid container spacing={2} alignItems='center'>
                <Grid item>
                  <MTableToolbar {...copyProps} />
                </Grid>
              </Grid>
            </Grid>
            <RowButton
              right={
                <ButtonView
                  onClick={() => {
                    if (this.state.currentTab === 0) {
                      this.createRouteBillingDetailConfiguration();
                    } else {
                      this.createRouteBillingDetailTiers();
                    }
                  }}
                  text={`Create Billing Detail ${
                    this.state.currentTab === 0 ? 'Configuration' : 'tiers'
                  }`}
                  active={updating}
                />
              }
            />
          </Grid>
        </Grid>
      );
    };

    renderBillingDetailConfigurationList = (): ReactElement => {
      const { billingDetailsConfiguration, loading, updating } = this.state;

      const listTableColumns: IListTableColumn[] = [
        {
          field: 'ID',
          title: 'ID',
          renderType: 'normal',
          searchable: true,
          hidden: true,
          sorting: false,
        },
        {
          field: 'Name',
          title: 'Publisher',
          renderType: 'namelink',
          searchable: true,
          sorting: false,
        },
        { field: 'Product', title: 'Product', renderType: 'normal' },
        { field: 'IsTiered', title: 'Is Tiered?', renderType: 'normal', searchable: false },
        { field: 'MinPrice', title: 'Min Price', renderType: 'normal', searchable: false },
        {
          field: 'CustomCampaignPrice',
          title: 'Custom Campaign Price',
          renderType: 'normal',
          searchable: false,
        },
        {
          field: 'CustomVastPrice',
          title: 'Custom Vast Price',
          renderType: 'normal',
          searchable: false,
        },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      const listTableData: IBillingDetailConfigurationRowData[] = billingDetailsConfiguration.map(
        (configuration: IBillingDetailConfiguration) => {
          return {
            ID: configuration.ID.toString(),
            Name: this.generateBillingDetailName(configuration),
            Product: configuration.Product,
            IsTiered: EBool.toBoolString(configuration.IsTiered),
            MinPrice:
              configuration.HasMinPrice === EBool.EBool_TRUE
                ? configuration.MinPrice.toString()
                : 'N/A',
            CustomCampaignPrice:
              configuration.HasCustomCampaignPrice === EBool.EBool_TRUE
                ? configuration.CustomCampaignPrice.toString()
                : 'N/A',
            CustomVastPrice:
              configuration.HasCustomVastPrice === EBool.EBool_TRUE
                ? configuration.CustomVastPrice.toString()
                : 'N/A',
          } as IBillingDetailConfigurationRowData;
        },
      );

      return (
        <Grid container direction='column'>
          <Grid item style={{ marginLeft: 15, width: '100%' }}>
            <ListTable
              title={''}
              columns={listTableColumns}
              data={listTableData}
              isLoading={loading}
              isUpdating={updating}
              renderCustomToolbar={this.renderCustomBillingDetailsToolbar}
              handleSetUpdating={this.handleSetUpdatingBillingDetailConfiguration}
              getItemSettingRoute={this.getBillingDetailConfigurationRoute}
              handleDeleteItem={this.deleteBillingDetailConfiguration}
              fixedEnabled={true}
              pageSize={10}
              readRoleName={'read_audience_manager'}
              writeRoleName={'write_audience_manager'}
            />
          </Grid>
        </Grid>
      );
    };

    renderBillingDetailTiersList = (): ReactElement => {
      const { billingDetailsTiers, loading, updating } = this.state;

      const listTableColumns: IListTableColumn[] = [
        {
          field: 'ID',
          title: 'ID',
          renderType: 'normal',
          searchable: true,
          hidden: true,
          sorting: false,
        },
        {
          field: 'Name',
          title: 'Publisher',
          renderType: 'namelink',
          searchable: true,
          sorting: false,
        },
        { field: 'Product', title: 'Product', renderType: 'normal' },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];

      const listTableData: IBillingDetailTiersRowData[] = billingDetailsTiers.map(
        (tiers: IBillingDetailTiers) => {
          return {
            ID: tiers.ID.toString(),
            Name: this.generateBillingDetailName(tiers),
            Product: tiers.Product,
          } as IBillingDetailTiersRowData;
        },
      );

      return (
        <Grid container direction='column'>
          <Grid item style={{ marginLeft: 15, width: '100%' }}>
            <ListTable
              title={''}
              columns={listTableColumns}
              data={listTableData}
              isLoading={loading}
              isUpdating={updating}
              renderCustomToolbar={this.renderCustomBillingDetailsToolbar}
              handleSetUpdating={this.handleSetUpdatingBillingDetailTiers}
              getItemSettingRoute={this.getBillingDetailTiersRoute}
              handleDeleteItem={this.deleteBillingDetailTiers}
              fixedEnabled={true}
              pageSize={10}
              readRoleName={'read_audience_manager'}
              writeRoleName={'write_audience_manager'}
            />
          </Grid>
        </Grid>
      );
    };

    renderTabs = (): ReactElement => {
      const tabs: ITab[] = [
        {
          label: 'Configuration',
          render: this.renderBillingDetailConfigurationList,
        },
        {
          label: 'Tiers',
          render: this.renderBillingDetailTiersList,
        },
      ];
      return (
        <SettingsTabs
          marginTop={0}
          paddingRight={10}
          tabs={tabs}
          setSettingsTabsCurrentTab={this.setSettingsTabsCurrentTab}
        />
      );
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { snackbarError, snackbarMessage } = this.state;

      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {this.renderTabs()}
            <SnackbarView
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
